/**
 * What to do when placement is refused because a source file is gone. Resume reconciles the
 * same thing, but a tab left open for days never re-runs it. Pure so it tests without a router or store;
 * `PlaceOrderHandler` applies it.
 */

import type { ConfigurationFlowId } from 'models/purchaseFlowRevamp'
import type { MissingSessionFiles } from 'models/purchaseFlow/missingSessionFiles'

/** A `(cartItemId, flowId)` tuple, as `resolveTabsFromCartAndCatalogue` emits them. */
export interface ConfigTarget {
  cartItemId: string
  flowId: ConfigurationFlowId
}

export interface MissingFileRecoveryPlan {
  /** Dead references to drop, so a retry cannot re-send them. */
  clear: Array<{ cartItemId: string, filename: string }>
  /** Configurations that name a dead document, rewritten without it, so the page asks for it again. */
  forget: Array<ConfigTarget & { next: unknown }>
  /** Where to send the client, or `null` for the catalogue when no configuration page owns the file. */
  target: ConfigTarget | null
}

const POWER_OF_ATTORNEY = 'POWER_OF_ATTORNEY'

/**
 * Matched by `gcFilename`, not `productCode`: the store keys uploads by the exact name the order sent, and
 * a line ordered twice makes a productCode→line mapping ambiguous. A file the store does not hold (a PoA,
 * in its own FileAPI scope) clears nothing and is still named in the message.
 */
export const planMissingFileRecovery = (
  missing: MissingSessionFiles,
  attachments: Readonly<Record<string, ReadonlyArray<{ gcFilename: string }>>>,
  tabs: ReadonlyArray<ConfigTarget>,
  configs: Readonly<Record<string, unknown>> = {}
): MissingFileRecoveryPlan => {
  const goneFilenames = new Set(missing.files.map((file) => file.filename))

  const clear: Array<{ cartItemId: string, filename: string }> = []
  for (const [cartItemId, list] of Object.entries(attachments)) {
    for (const attachment of list) {
      if (goneFilenames.has(attachment.gcFilename)) clear.push({ cartItemId, filename: attachment.gcFilename })
    }
  }

  // A document is not an attachment: its module records the stored filename in the configuration
  // (power of attorney, certificate photos, floor-plan logo), which would keep the page complete.
  const forget: Array<ConfigTarget & { next: unknown }> = []
  for (const [key, config] of Object.entries(configs)) {
    const next = withoutFilenames(config, goneFilenames)
    if (next === config) continue
    const separator = key.indexOf('|')
    forget.push({ cartItemId: key.slice(0, separator), flowId: key.slice(separator + 1) as ConfigurationFlowId, next })
  }

  // Prefer the page that owns a line we actually cleared — that is where the client re-uploads.
  const tabOf = ({ cartItemId, flowId }: { cartItemId: string, flowId?: string }) =>
    tabs.find((tab) => tab.cartItemId === cartItemId && (!flowId || tab.flowId === flowId))
  const clearedTarget = [...clear, ...forget].map(tabOf).find((tab): tab is ConfigTarget => Boolean(tab))
  if (clearedTarget) return { clear, forget, target: clearedTarget }

  // No cleared line has a configuration page. An order-level document has one all the same, and it is
  // the only surface that collects it.
  const needsPowerOfAttorney = missing.files.some((file) => file.orderDocumentType === POWER_OF_ATTORNEY)
  if (needsPowerOfAttorney) {
    const poaTab = tabs.find((tab) => tab.flowId === 'power_of_attorney')
    if (poaTab) return { clear, forget, target: poaTab }
  }

  return { clear, forget, target: null }
}

/**
 * The value with every string naming a gone file removed — a lone field emptied, an array filtered —
 * or the very same value when it names none, so callers can tell an untouched configuration apart.
 */
export const withoutFilenames = (value: unknown, gone: ReadonlySet<string>): unknown => {
  if (Array.isArray(value)) {
    const kept = value.filter((entry) => !(typeof entry === 'string' && gone.has(entry)))
    const mapped = kept.map((entry) => withoutFilenames(entry, gone))
    const changed = kept.length !== value.length || mapped.some((entry, index) => entry !== kept[index])
    return changed ? mapped : value
  }
  if (value && typeof value === 'object') {
    let changed = false
    const next: Record<string, unknown> = {}
    for (const [field, entry] of Object.entries(value)) {
      const replaced = typeof entry === 'string' && gone.has(entry) ? undefined : withoutFilenames(entry, gone)
      if (replaced !== entry) changed = true
      next[field] = replaced
    }
    return changed ? next : value
  }
  return value
}
